import { useNavigate, useSearchParams } from 'react-router-dom';
import { NavigateOptions } from 'react-router-dom';

/**
 * Enhanced navigate hook that preserves URL search parameters
 *
 * Problem: Standard navigate() loses query params like ?view=totem
 * Solution: This hook automatically appends current search params to all navigation
 *
 * Usage:
 * const navigate = useNavigateWithParams();
 * navigate('/rooms'); // Becomes /rooms?view=totem (if currently in totem mode)
 * navigate('/rooms/123'); // Becomes /rooms/123?view=totem
 *
 * @returns Enhanced navigate function that preserves search params
 */
export function useNavigateWithParams() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return (to: string | number, options?: NavigateOptions) => {
    // If navigating by delta (e.g., -1 for back), just pass through
    if (typeof to === 'number') {
      navigate(to);
      return;
    }

    // Build the new path with preserved search params
    const currentParams = searchParams.toString();
    const separator = to.includes('?') ? '&' : '?';
    const newPath = currentParams ? `${to}${separator}${currentParams}` : to;

    navigate(newPath, options);
  };
}
